import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  useDeleteSubtask,
  useReorderSubtasks,
} from "../../Controllers/TaskManagementController";
import { toast } from "react-toastify";

const Subtasks = ({
  subTasks,
  parentStatus,
  onSubtaskComplete,
  onSubtaskDelete,
  isParentCompleted = false,
  isDisabled = false,
  taskId,
  onSubtaskReorder,
}) => {
  const { mutate: deleteSubtask } = useDeleteSubtask();
  const { mutate: reorderSubtasks } = useReorderSubtasks();
  const [openMenuId, setOpenMenuId] = useState(null);
  const menusRef = useRef({});
  const [optimisticSubtasks, setOptimisticSubtasks] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const refs = Object.values(menusRef.current || {});
      if (refs.every((el) => el && !el.contains(event.target))) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Don't clear optimistic state when subtasks prop changes
  // We want to maintain the reordered state locally until user navigates away
  // When user comes back, server will return the updated order

  // Hooks must be called at the top level, before any conditional returns
  if (!subTasks || subTasks.length === 0) {
    return null;
  }

  // Use optimistic state if available, otherwise use original subtasks
  const currentSubtasks = optimisticSubtasks || subTasks;

  // Sort subtasks by their order field to ensure proper display order
  const sortedSubTasks = [...currentSubtasks].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  const handleSubtaskToggle = (subTaskId) => {
    if (isDisabled) return;
    // Don't allow operations if parent task is completed
    if (isParentCompleted) {
      toast.warning("Cannot modify subtasks of completed tasks");
      return;
    }
    if (isDisabled) return;
    onSubtaskComplete(subTaskId);
  };

  const handleDelete = (subTaskId) => {
    // Don't allow operations if parent task is completed
    if (isParentCompleted) {
      toast.warning("Cannot modify subtasks of completed tasks");
      return;
    }
    if (isDisabled) return;

    deleteSubtask(subTaskId, {
      onSuccess: () => {
        toast.success("Subtask deleted successfully");
        setOpenMenuId(null);
        onSubtaskDelete();
      },
    });
  };

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Store original order for potential rollback
    const originalSubtasks = [...subTasks];

    // Create new order array based on the drag result
    const newOrder = Array.from(sortedSubTasks);
    const [removed] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, removed);

    // Update subtasks with new order values
    const reorderedSubtasks = newOrder.map((subtask, index) => ({
      ...subtask,
      order: index + 1,
    }));

    // OPTIMISTIC UPDATE: Immediately update UI
    setOptimisticSubtasks(reorderedSubtasks);

    // Extract subtask IDs in the new order for API call
    const subtaskIds = newOrder.map((subtask) => subtask.id);

    // Call the reorder API
    reorderSubtasks(
      {
        taskId: parseInt(taskId), // Convert string to integer
        order: subtaskIds,
      },
      {
        onSuccess: (response) => {
          // API success - keep the optimistic state permanently
          // Don't clear optimisticSubtasks - we want to maintain the reordered state locally
          // Server has been updated silently, so when user navigates away and comes back,
          // the server will return the updated order and everything will be consistent
          // toast.success("Subtasks reordered successfully");
        },
        onError: (error) => {
          // API failure - rollback to original order
          setOptimisticSubtasks(null);
          toast.error(
            "Failed to reorder subtasks - reverted to original order",
          );
        },
      },
    );
  };

  const getCategoryStyle = (category) => {
    const normalizedCategory = category.replace(/\s+/g, " ").trim();

    switch (normalizedCategory) {
      case "Urgent Important":
        return {
          border: "2px solid #dc3545",
          color: "#dc3545",
          backgroundColor: "transparent",
        };
      case "Urgent Not Important":
        return {
          border: "2px solid #fd7e14",
          color: "#fd7e14",
          backgroundColor: "transparent",
        };
      case "Important Not Urgent":
        return {
          border: "2px solid #ffc107",
          color: "#ffc107",
          backgroundColor: "transparent",
        };
      case "Not Important Not Urgent":
        return {
          border: "2px solid #6c757d",
          color: "#6c757d",
          backgroundColor: "transparent",
        };
      default:
        return {
          border: "2px solid #6c757d",
          color: "#6c757d",
          backgroundColor: "transparent",
        };
    }
  };

  // Fixed function to get indicator color based on parent category
  const getIndicatorColor = (parentCategory) => {
    // Handle the double spaces in category names
    const normalizedCategory = parentCategory.replace(/\s+/g, " ").trim();

    switch (normalizedCategory) {
      case "Urgent Important":
        return "red";
      case "Urgent Not Important":
        return "orange";
      case "Important Not Urgent":
        return "yellow";
      case "Not Important Not Urgent":
        return "gray";
      default:
        return "gray";
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <SubtasksContainer>
        <Droppable droppableId={`subtasks-${taskId}`} type="SUBTASK">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginTop: "16px",
                minHeight: "20px",
                backgroundColor: snapshot.isDraggingOver
                  ? "rgba(255, 255, 255, 0.02)"
                  : "transparent",
                borderRadius: "8px",
                padding: snapshot.isDraggingOver ? "8px" : "0",
                transition: "all 0.2s ease",
              }}
            >
              {sortedSubTasks.map((subTask, index) => (
                <Draggable
                  key={subTask.id}
                  draggableId={`subtask-${subTask.id}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="sub-task-card"
                      style={{
                        ...provided.draggableProps.style,
                        transform: snapshot.isDragging
                          ? provided.draggableProps.style?.transform
                          : "none",
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        boxShadow: snapshot.isDragging
                          ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                          : "none",
                        cursor: snapshot.isDragging ? "grabbing" : "grab",
                      }}
                    >
                      <div
                        className={`kanban-subtask-indicator ${getIndicatorColor(
                          parentStatus,
                        )}`}
                      ></div>
                      <div
                        className="d-flex flex-column"
                        style={{ gap: "12px" }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span
                            className="text-body-b3"
                            style={{ color: "#c8ced3" }}
                          >
                            {subTask.title}
                          </span>
                          <div className="d-flex gap-2 align-items-start">
                            <img
                              className="subtask-more-button"
                              src="/icons/task-management-more.svg"
                              alt="More"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isParentCompleted) {
                                  toast.warning(
                                    "Cannot modify subtasks of completed tasks",
                                  );
                                  return;
                                }
                                setOpenMenuId((prev) =>
                                  prev === subTask.id ? null : subTask.id,
                                );
                              }}
                              style={{
                                cursor: isParentCompleted
                                  ? "not-allowed"
                                  : "pointer",
                                opacity: isParentCompleted ? 0.6 : 1,
                              }}
                            />
                            {openMenuId === subTask.id && (
                              <MenuContainer
                                ref={(el) =>
                                  (menusRef.current[subTask.id] = el)
                                }
                              >
                                <MenuItem
                                  onClick={() => handleDelete(subTask.id)}
                                >
                                  Delete
                                </MenuItem>
                              </MenuContainer>
                            )}
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div
                            style={{
                              ...getCategoryStyle(parentStatus),
                              fontSize: "12px",
                              padding: "2px 4px",
                              borderRadius: "4px",
                            }}
                          >
                            {parentStatus}
                          </div>
                          <div
                            className="subtask-status"
                            onClick={() => handleSubtaskToggle(subTask.id)}
                            style={{
                              cursor: isParentCompleted
                                ? "not-allowed"
                                : "pointer",
                              opacity: isParentCompleted ? 0.6 : 1,
                            }}
                          >
                            {subTask.status === "completed" ? (
                              <div className="subtask-done">
                                <img
                                  src="/icons/calender-circle-tick.svg"
                                  alt="Done"
                                />
                              </div>
                            ) : (
                              <div className="subtask-not-done">
                                <div
                                  className="done-indicator-subtask"
                                  style={{ borderRadius: "100%" }}
                                ></div>
                                <span>Done</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </SubtasksContainer>
    </DragDropContext>
  );
};

const SubtasksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      max-height: 500px;
      transform: translateY(0);
    }
  }

  .sub-task-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    padding: 12px;
    position: relative;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    .kanban-subtask-indicator {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      border-radius: 8px 0 0 8px;

      &.red {
        background: #dc3545; /* red for Urgent Important */
      }
      &.orange {
        background: #fd7e14; /* orange for Urgent Not Important */
      }
      &.yellow {
        background: #ffc107; /* yellow for Important Not Urgent */
      }
      &.gray {
        background: #6c757d; /* gray for Not Important Not Urgent */
      }
    }

    .subtask-status {
      display: flex;
      align-items: center;
      gap: 4px;

      .done-indicator-subtask {
        width: 12px;
        height: 12px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 2px;
      }
    }
  }
`;

const MenuContainer = styled.div`
  position: absolute;
  right: 8px;
  top: 28px;
  background: #1b2c3a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 6px 0;
  z-index: 10;
  min-width: 120px;
`;

const MenuItem = styled.div`
  padding: 8px 12px;
  color: #e6ecf1;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

export default Subtasks;
